import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, 
  Brain, 
  CheckSquare, 
  BarChart3, 
  Sparkles,
  ArrowRight,
  BookOpen,
  Calendar,
  Users,
  Zap,
  Compass,
  Lightbulb,
  TrendingUp,
  Clock,
  CheckCircle2,
  Star,
  Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';

const Landing = () => {
  const coreValues = [
    {
      icon: <Compass className="w-8 h-8" />,
      title: 'Clarity of Direction',
      description: 'Know exactly where you\'re headed. Transform vague aspirations into clear, actionable strategic objectives that guide every decision.',
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Grounded Focus',
      description: 'Stay rooted in what matters most. Daily prompts and reflections keep you connected to your core purpose and strategic intent.',
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Intentional Action',
      description: 'Every task connects to your bigger vision. AI-powered next actions ensure you\'re always moving toward your goals with purpose.',
    },
  ];

  const features = [
    {
      icon: <Calendar className="w-6 h-6" />,
      title: 'Daily Prompts',
      description: 'Start each day grounded with reflective questions that sharpen your strategic thinking and maintain clarity of purpose.',
      highlight: 'Build strategic habits',
    },
    {
      icon: <CheckSquare className="w-6 h-6" />,
      title: 'Next Actions',
      description: 'Never wonder "what should I do next?" AI analyzes your goals and priorities to surface the most impactful actions.',
      highlight: 'AI-prioritized tasks',
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Strategic Goals',
      description: 'Set ambitious objectives with clear action items. Track progress and celebrate wins along your strategic journey.',
      highlight: 'Proven frameworks',
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: 'AI Strategy Coach',
      description: 'Get personalized guidance whenever you need it. Ask questions, explore scenarios, and refine your strategic approach.',
      highlight: '24/7 coaching',
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Learning Modules',
      description: 'Master strategic planning through structured lessons covering frameworks, execution strategies, and leadership principles.',
      highlight: '5 comprehensive modules',
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Strategic Scorecard',
      description: 'Visualize your progress with analytics that show trends, completion rates, and areas needing attention.',
      highlight: 'Data-driven insights',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Ground Yourself',
      description: 'Start each day with a daily prompt. These reflective questions help you reconnect with your purpose and set clear intentions.',
      icon: <Compass className="w-6 h-6" />,
    },
    {
      step: '02',
      title: 'Define Your Vision',
      description: 'Set meaningful goals with clear action items. Break down your big vision into achievable milestones with measurable outcomes.',
      icon: <Target className="w-6 h-6" />,
    },
    {
      step: '03',
      title: 'Take Intentional Action',
      description: 'Follow AI-suggested next actions that align with your priorities. Every task moves you closer to your strategic objectives.',
      icon: <CheckCircle2 className="w-6 h-6" />,
    },
    {
      step: '04',
      title: 'Reflect & Grow',
      description: 'Review your progress, learn from insights, and continuously refine your approach with AI coaching and analytics.',
      icon: <TrendingUp className="w-6 h-6" />,
    },
  ];

  const testimonials = [
    {
      quote: "The daily prompts changed everything for me. I used to feel scattered, jumping from task to task. Now I start each day with clarity and purpose.",
      author: "Sarah Chen",
      role: "Startup Founder",
      highlight: "From scattered to focused"
    },
    {
      quote: "The 'Next Actions' feature is like having a strategic advisor. It cuts through the noise and shows me exactly what deserves my attention right now.",
      author: "Marcus Johnson",
      role: "Product Manager",
      highlight: "Clear priorities daily"
    },
    {
      quote: "This platform taught me that strategy isn't just for big companies. The learning modules gave me frameworks I use every day.",
      author: "Emily Rodriguez",
      role: "Business Student",
      highlight: "Practical strategic skills"
    }
  ];

  const intentStatements = [
    "Move from reactive to proactive",
    "Transform overwhelm into clarity",
    "Connect daily tasks to bigger vision",
    "Build lasting strategic habits",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] noise-overlay">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-slate-200 dark:border-[#1F2937]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-[#26C6DA]" style={{ fontFamily: 'Outfit' }}>
                Strategic Architect
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-[#26C6DA] hover:bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]/10 rounded-full" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-white rounded-full px-6" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Enhanced */}
      <section className="pt-32 pb-20 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]/10 text-[#26C6DA] text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Your Strategic Operating System
              </div>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                From Overwhelm<br />
                to <span className="text-gradient">Clarity.</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 max-w-xl leading-relaxed">
                Strategic Architect helps you cut through the noise, ground yourself in purpose, and take intentional action every day. 
                With AI-powered guidance, daily prompts, and clear next actions, you'll finally know exactly where you're going—and how to get there.
              </p>
              
              {/* Intent Statements */}
              <div className="grid grid-cols-2 gap-3">
                {intentStatements.map((statement, index) => (
                  <div key={index} className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                    <CheckCircle2 className="w-5 h-5 text-[#26C6DA] flex-shrink-0" />
                    <span className="text-sm">{statement}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="hero-cta-btn">
                    Start Your Journey
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="border-slate-200 dark:border-[#1F2937] text-[#26C6DA] hover:bg-[#0A0A0A] rounded-full px-8 py-6 text-lg" data-testid="hero-signin-btn">
                    Sign In
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute inset-0 bg-gradient-to-br from-[#0F5156]/20 to-[#26C6DA]/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                alt="Strategic Planning Session" 
                className="relative rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Strategic Architect Section */}
      <section className="py-20 px-6 md:px-12 bg-white dark:bg-[#111827]/50" data-testid="what-is-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-6" style={{ fontFamily: 'Outfit' }}>
            What is Strategic Architect?
          </h2>
          <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
            Strategic Architect is more than a productivity tool—it's a <strong>strategic operating system</strong> designed to help entrepreneurs, 
            teams, and forward-thinking individuals move from chaos to clarity. We combine <strong>structured learning</strong>, 
            <strong>daily grounding practices</strong>, and <strong>AI-powered guidance</strong> to help you not just plan, but truly execute on what matters most.
          </p>
          <div className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]/5 rounded-2xl p-8 border border-[#0F5156]/10">
            <p className="text-xl text-[#26C6DA] font-medium italic" style={{ fontFamily: 'Outfit' }}>
              "Strategy without execution is a daydream. Execution without strategy is a nightmare."
            </p>
            <p className="text-slate-500 dark:text-slate-400 mt-2">— Japanese Proverb</p>
          </div>
        </div>
      </section>

      {/* Core Value Propositions */}
      <section className="py-24 px-6 md:px-12" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              The Three Pillars of Strategic Success
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              Every feature in Strategic Architect is designed around these core principles.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-3xl p-8 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0F5156] to-[#26C6DA] flex items-center justify-center text-white mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
                  {value.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-white/50 to-transparent" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              Your Daily Strategic Practice
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              A simple but powerful rhythm that transforms how you approach each day.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => (
              <div 
                key={index}
                className="relative bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6"
                data-testid={`step-card-${index}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl font-bold text-[#26C6DA]/20" style={{ fontFamily: 'Outfit' }}>
                    {step.step}
                  </span>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]/10 flex items-center justify-center text-[#26C6DA]">
                    {step.icon}
                  </div>
                </div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {step.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Deep Dive */}
      <section className="py-24 px-6 md:px-12" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              Powerful Tools for Strategic Execution
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              Everything you need to learn, plan, and execute your strategy—all in one place.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]/10 flex items-center justify-center text-[#26C6DA]">
                    {feature.icon}
                  </div>
                  <span className="text-xs font-medium text-[#26C6DA] bg-[#26C6DA]/10 px-3 py-1 rounded-full">
                    {feature.highlight}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {feature.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Prompts & Next Actions Highlight */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]" data-testid="prompts-actions-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Daily Prompts */}
            <div className="bg-slate-100 dark:bg-[#1A1A2E] backdrop-blur-md rounded-3xl p-8 border border-slate-200 dark:border-[#1F2937]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                  Daily Prompts
                </h3>
              </div>
              <p className="text-white/80 mb-6 leading-relaxed">
                Each morning, receive a thought-provoking question designed to ground you in your strategic purpose. 
                These prompts help you reflect, recalibrate, and approach your day with intention.
              </p>
              <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl p-4 border border-white/10">
                <p className="text-white/60 text-sm mb-2">Today's Prompt Example:</p>
                <p className="text-white text-lg italic">
                  "What is the one thing you could accomplish today that would make everything else easier or unnecessary?"
                </p>
              </div>
              <ul className="mt-6 space-y-3">
                <li className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#26C6DA]" />
                  <span>7 unique prompts rotating weekly</span>
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#26C6DA]" />
                  <span>Save your reflections for future review</span>
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#26C6DA]" />
                  <span>Build a habit of strategic thinking</span>
                </li>
              </ul>
            </div>

            {/* Next Actions */}
            <div className="bg-slate-100 dark:bg-[#1A1A2E] backdrop-blur-md rounded-3xl p-8 border border-slate-200 dark:border-[#1F2937]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
                  AI-Powered Next Actions
                </h3>
              </div>
              <p className="text-white/80 mb-6 leading-relaxed">
                Never feel lost about what to do next. Our AI analyzes your goals, tasks, and priorities 
                to surface the most impactful actions you should take right now.
              </p>
              <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl p-4 border border-white/10">
                <p className="text-white/60 text-sm mb-2">Suggested Next Actions:</p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-white">
                    <div className="w-2 h-2 rounded-full bg-[#26C6DA]"></div>
                    Review Q1 goal progress before team meeting
                  </li>
                  <li className="flex items-center gap-2 text-white">
                    <div className="w-2 h-2 rounded-full bg-[#26C6DA]"></div>
                    Complete Module 3: Strategic Execution
                  </li>
                </ul>
              </div>
              <ul className="mt-6 space-y-3">
                <li className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#26C6DA]" />
                  <span>Priorities aligned to your goals</span>
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#26C6DA]" />
                  <span>Updates as you complete tasks</span>
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[#26C6DA]" />
                  <span>Reduces decision fatigue</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For Section */}
      <section className="py-24 px-6 md:px-12" data-testid="who-for-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              Built For Strategic Thinkers
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
              Whether you're leading a startup, managing a team, or charting your own course—Strategic Architect meets you where you are.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Zap />, title: 'Entrepreneurs', desc: 'Build and scale your vision with strategic clarity' },
              { icon: <Users />, title: 'Team Leaders', desc: 'Align your team around shared strategic goals' },
              { icon: <TrendingUp />, title: 'Professionals', desc: 'Advance your career with intentional growth' },
              { icon: <BookOpen />, title: 'Students', desc: 'Learn strategic frameworks that last a lifetime' },
            ].map((persona, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6 text-center"
                data-testid={`persona-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6]/10 flex items-center justify-center text-[#26C6DA] mx-auto mb-4">
                  {persona.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                  {persona.title}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {persona.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-6 md:px-12 bg-white dark:bg-[#111827]/50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              Stories of Transformation
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400">
              See how Strategic Architect is helping people achieve clarity and results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-8 relative"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="absolute top-4 right-4">
                  <Quote className="w-8 h-8 text-[#26C6DA]/10" />
                </div>
                <div className="mb-4">
                  <span className="text-xs font-medium text-[#26C6DA] bg-[#26C6DA]/10 px-3 py-1 rounded-full">
                    {testimonial.highlight}
                  </span>
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F5156] to-[#26C6DA] flex items-center justify-center text-white font-medium">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-slate-100">{testimonial.author}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12" data-testid="cta-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <div className="grid grid-cols-[repeat(20,1fr)] grid-rows-[repeat(20,1fr)] h-full w-full">
                {Array.from({ length: 400 }).map((_, i) => (
                  <div key={i} className="border-r border-b border-white/5"></div>
                ))}
              </div>
            </div>
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-[#1A1A2E] text-white/90 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Start Free Today
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
                Ready to Find Your Clarity?
              </h2>
              <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of strategic thinkers who've transformed how they plan, 
                execute, and achieve their most important goals.
              </p>
              <Link to="/register">
                <Button className="bg-white text-[#26C6DA] hover:bg-[#0A0A0A] rounded-full px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="cta-btn">
                  Begin Your Strategic Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <p className="text-white/60 text-sm mt-4">
                Free plan available. No credit card required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-slate-200 dark:border-[#1F2937]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-[#26C6DA]" style={{ fontFamily: 'Outfit' }}>
              Strategic Architect
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="text-slate-500 dark:text-slate-400 hover:text-[#26C6DA] transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-slate-500 dark:text-slate-400 hover:text-[#26C6DA] transition-colors">
              Privacy Policy
            </Link>
            <a href="mailto:strategicarchitect@blueprintlifestrategies.com" className="text-slate-500 dark:text-slate-400 hover:text-[#26C6DA] transition-colors">
              Contact
            </a>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            © {new Date().getFullYear()} Strategic Architect. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
